<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useInternalCall,
  bindInternalCallEvents,
  getLocalStream,
  getRemoteStream,
} from 'dashboard/composables/useInternalCall';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const { t } = useI18n();
const { state, answerCall, declineCall, endCall, toggleMute, toggleCamera } =
  useInternalCall();

bindInternalCallEvents();

const isVideo = computed(() => state.callType === 'video');
const showVideoStage = computed(
  () => isVideo.value && state.status === 'active'
);

const localVideoRef = ref(null);
const remoteContainerRef = ref(null);

// Re-bind MediaStreams to <video> elements whenever streams change (streamEpoch
// bumps on getUserMedia / ontrack / peer add/remove / cleanup).
const bindVideos = async () => {
  await nextTick();
  if (localVideoRef.value) localVideoRef.value.srcObject = getLocalStream();
  if (!remoteContainerRef.value) return;
  state.peerIds.forEach(peerId => {
    const el = remoteContainerRef.value.querySelector(
      `video[data-peer-id="${peerId}"]`
    );
    if (el) el.srcObject = getRemoteStream(peerId);
  });
};
watch(
  () => [state.streamEpoch, showVideoStage.value],
  () => {
    if (showVideoStage.value) bindVideos();
  }
);

const visible = computed(() => state.status !== 'idle');
const peerName = computed(
  () => state.peer?.name || t('INTERNAL_CHATS.CALL.UNKNOWN_PEER')
);
const headerTitle = computed(() =>
  state.isGroup ? t('INTERNAL_CHATS.CALL.GROUP_TITLE') : peerName.value
);

const elapsed = ref(0);
let timer = null;
const stopTimer = () => {
  if (timer) clearInterval(timer);
  timer = null;
};
const startTimer = () => {
  stopTimer();
  elapsed.value = 0;
  timer = setInterval(() => {
    elapsed.value += 1;
  }, 1000);
};
const elapsedLabel = computed(() => {
  const m = String(Math.floor(elapsed.value / 60)).padStart(2, '0');
  const s = String(elapsed.value % 60).padStart(2, '0');
  return `${m}:${s}`;
});

const statusLabel = computed(() => {
  switch (state.status) {
    case 'incoming':
      return t('INTERNAL_CHATS.CALL.INCOMING');
    case 'outgoing':
      return t('INTERNAL_CHATS.CALL.CALLING');
    case 'active':
      return state.isGroup
        ? t('INTERNAL_CHATS.CALL.PARTICIPANTS', {
            count: state.peerIds.length + 1,
          })
        : elapsedLabel.value;
    default:
      return '';
  }
});

const onAnswer = () => {
  startTimer();
  answerCall();
};
const onDecline = () => declineCall();
const onHangup = () => {
  stopTimer();
  endCall();
};

onMounted(() => {
  if (state.status === 'active') startTimer();
});
onBeforeUnmount(stopTimer);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="fixed bottom-4 right-4 z-50 w-72 rounded-xl bg-n-solid-2 border border-n-weak shadow-lg p-4 flex flex-col items-center gap-3"
      role="dialog"
      :aria-label="t('INTERNAL_CHATS.CALL.WIDGET_LABEL')"
    >
      <Avatar
        v-if="!showVideoStage"
        :name="peerName"
        :src="state.peer?.avatar_url || ''"
        :size="56"
        rounded-full
      />
      <div class="text-center">
        <p class="text-sm font-semibold text-n-slate-12 truncate">
          {{ headerTitle }}
        </p>
        <p class="text-xs text-n-slate-10 mt-0.5">{{ statusLabel }}</p>
      </div>

      <!-- Video stage (video call, active): remote grid + local PiP -->
      <div v-if="showVideoStage" class="relative w-full">
        <div
          ref="remoteContainerRef"
          class="grid gap-1"
          :class="state.peerIds.length > 1 ? 'grid-cols-2' : 'grid-cols-1'"
        >
          <div
            v-for="peerId in state.peerIds"
            :key="peerId"
            class="aspect-video rounded-lg overflow-hidden bg-n-slate-3"
          >
            <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
            <video
              :data-peer-id="peerId"
              autoplay
              playsinline
              class="w-full h-full object-cover"
            />
          </div>
        </div>
        <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
        <video
          ref="localVideoRef"
          autoplay
          playsinline
          muted
          class="absolute bottom-1 right-1 w-16 rounded-md border border-n-weak object-cover"
        />
      </div>

      <!-- Incoming: accept / decline -->
      <div v-if="state.status === 'incoming'" class="flex items-center gap-4">
        <button
          class="size-11 rounded-full bg-n-ruby-9 text-white flex items-center justify-center hover:bg-n-ruby-10"
          :aria-label="t('INTERNAL_CHATS.CALL.DECLINE')"
          @click="onDecline"
        >
          <span class="i-lucide-phone-off size-5" />
        </button>
        <button
          class="size-11 rounded-full bg-n-teal-9 text-white flex items-center justify-center hover:bg-n-teal-10"
          :aria-label="t('INTERNAL_CHATS.CALL.ANSWER')"
          @click="onAnswer"
        >
          <span class="i-lucide-phone size-5" />
        </button>
      </div>

      <!-- Outgoing / active: mute + camera (active+video) + hangup -->
      <div v-else class="flex items-center gap-4">
        <button
          v-if="state.status === 'active'"
          class="size-11 rounded-full bg-n-alpha-2 text-n-slate-12 flex items-center justify-center hover:bg-n-alpha-3"
          :aria-label="
            state.muted
              ? t('INTERNAL_CHATS.CALL.UNMUTE')
              : t('INTERNAL_CHATS.CALL.MUTE')
          "
          @click="toggleMute"
        >
          <span
            :class="state.muted ? 'i-lucide-mic-off' : 'i-lucide-mic'"
            class="size-5"
          />
        </button>
        <button
          v-if="state.status === 'active' && isVideo"
          class="size-11 rounded-full bg-n-alpha-2 text-n-slate-12 flex items-center justify-center hover:bg-n-alpha-3"
          :aria-label="
            state.cameraOff
              ? t('INTERNAL_CHATS.CALL.CAMERA_ON')
              : t('INTERNAL_CHATS.CALL.CAMERA_OFF')
          "
          @click="toggleCamera"
        >
          <span
            :class="state.cameraOff ? 'i-lucide-video-off' : 'i-lucide-video'"
            class="size-5"
          />
        </button>
        <button
          class="size-11 rounded-full bg-n-ruby-9 text-white flex items-center justify-center hover:bg-n-ruby-10"
          :aria-label="t('INTERNAL_CHATS.CALL.HANG_UP')"
          @click="onHangup"
        >
          <span class="i-lucide-phone-off size-5" />
        </button>
      </div>
    </div>
  </Teleport>
</template>
